/**
 * Recommendation Panel
 *
 * Container for contextual recommendations after a note reaches COMPLETED.
 * Renders below the pipeline flow (same position as DemoReviewPanel) with
 * a cyan-themed border to distinguish from the yellow review panel.
 *
 * Shows a skeleton loader while recommendations are generating,
 * then renders RecommendationCards with individual RatingWidgets.
 */

import { Lightbulb } from "lucide-react";
import type {
  Recommendation,
  RecommendationRating,
} from "../../types/recommendation";
import { RecommendationCard } from "./RecommendationCard";

interface RecommendationPanelProps {
  noteId: string;
  recommendations: Recommendation[];
  isLoading: boolean;
  onRate: (recId: string, rating: RecommendationRating) => void;
  className?: string;
}

/** Skeleton placeholder while recommendations are generating */
function SkeletonCard() {
  return (
    <div className="flex items-start gap-3 py-3 px-3 rounded-md bg-bg-primary/50 border border-border/30 animate-pulse">
      <div className="w-8 h-8 rounded-md bg-bg-secondary" />
      <div className="flex-1 space-y-2">
        <div className="h-2.5 w-16 rounded bg-bg-secondary" />
        <div className="h-3 w-3/4 rounded bg-bg-secondary" />
        <div className="h-2.5 w-1/2 rounded bg-bg-secondary" />
      </div>
      <div className="flex gap-0.5">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="w-7 h-7 rounded bg-bg-secondary" />
        ))}
      </div>
    </div>
  );
}

export function RecommendationPanel({
  recommendations,
  isLoading,
  onRate,
  className = "",
}: RecommendationPanelProps) {
  const hasRecs = recommendations.length > 0;

  if (!isLoading && !hasRecs) {
    return null;
  }

  return (
    <div
      className={`border-t border-accent-cyan/30 bg-accent-cyan/5 px-4 py-3 ${className}`}
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Lightbulb className="w-4 h-4 text-accent-cyan" />
        <h3 className="text-xs font-semibold text-text-primary">
          Contextual Recommendations
        </h3>
        {hasRecs && (
          <span className="text-[10px] text-text-muted font-mono">
            {recommendations.length} suggestion
            {recommendations.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="space-y-2">
        {isLoading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : (
          recommendations.map((rec) => (
            <RecommendationCard
              key={rec.id}
              recommendation={rec}
              onRate={onRate}
            />
          ))
        )}
      </div>

      {/* Footer hint */}
      {hasRecs && (
        <p className="text-[10px] text-text-muted mt-3">
          Rate recommendations to improve future suggestions
        </p>
      )}
    </div>
  );
}
